import { APP_AVAILABLE } from "./app-availability";
import {
  CONTENT_REVIEWER_ID,
  activeContentReviewer,
  type ContentReviewer,
} from "./content-reviewer";

export const SITE_URL = "https://ishvaram.com";
export const SITE_NAME = "Ishvaram";
export const SITE_LOGO = `${SITE_URL}/cdn/shop/files/1.png?v=1723014680&width=340`;

// App store anchors are entity-graph SAME_AS links (rule 26) — only advertise
// them when the app is publicly installable, else they 404 and weaken trust.
const APP_STORE_SAME_AS = [
  "https://play.google.com/store/apps/details?id=com.ishvaram.app",
  "https://apps.apple.com/app/id6743459479",
];

// 2026-07-27: the Wikidata anchor was REMOVED — Q139596352 was deleted on
// 2026-07-20 by an admin via Requests for Deletions ("Does not meet the
// notability policy"). Rule 26 forbids a SAME_AS that does not resolve: a
// crawler follows it, so a dead anchor is worse than an absent one. Re-add only
// once an entity exists again AND `sync_wikidata_to_schema.py` exits 0 — and
// per rule 26, sequence recreation AFTER press coverage or it fails WD:N again.
//
// Replaced by two owner-confirmed, resolving anchors (LinkedIn + Pinterest), so
// the entity graph is honestly above the G100 >=5 threshold rather than propped
// up by a 404. NOTE: an `x.com/ishvaram` account exists and is titled
// "Ishvaram" but is NOT ours (owner-confirmed 2026-07-27) — never add it. That
// is why an anchor needs owner confirmation, not just an HTTP 200.
export const SAME_AS: string[] = [
  ...(APP_AVAILABLE ? APP_STORE_SAME_AS : []),
  "https://ishvaram.com",
  "https://www.instagram.com/ishvaram.in/",
  "https://www.facebook.com/ishvaram.in",
  "https://www.youtube.com/channel/UCmLGFR2IpfqgoWaHuD-L9Vg",
  "https://www.linkedin.com/company/ishvaram/",
  "https://www.pinterest.com/ishvaramindia/",
  // Owner-confirmed 2026-07-30 (owner set the org avatar; we operate the org):
  // public org profile with README, populated the same day (rule 26).
  "https://github.com/Ishvaram-Business",
  // Owner-created + confirmed 2026-07-30. NOTE for the liveness guard: Crunchbase
  // 403s datacenter probes (bot wall) — that is the documented INCONCLUSIVE
  // class, never a dead-anchor alarm (rule 26).
  "https://www.crunchbase.com/organization/ishvaram",
  // Google Business Profile CID link (owner-confirmed 2026-07-30: derived from
  // the owner's live Maps place URL, KG mid /g/11xf9bmq8p; the dashboard
  // "business id" is a DIFFERENT namespace — never anchor that). Maps serves a
  // JS shell to datacenter probes: liveness-guard INCONCLUSIVE class (rule 26).
  "https://maps.google.com/?cid=10448390172209870661",
];

export const ORG = {
  "@type": "Organization",
  "@id": `${SITE_URL}#org`,
  name: SITE_NAME,
  url: SITE_URL,
  logo: {
    "@type": "ImageObject",
    url: SITE_LOGO,
  },
  // E-E-A-T: the entity's demonstrated expertise domain + its editorial/methodology
  // page (how facts are computed, what the AI never does). Honest, org-level — no
  // fabricated human (content-reviewer.ts stays owner-gated).
  knowsAbout: [
    "Vedic astrology",
    "Jyotish",
    "Kundali",
    "Panchang",
    "Muhurat",
    "Kundli Matching",
    "Dosha analysis",
    "Nakshatra",
    "Vimshottari Dasha",
  ],
  publishingPrinciples: `${SITE_URL}/about/`,
  // E-E-A-T contact signal (YMYL): a reachable support channel is a documented
  // quality-rater expectation. Email only — the same address already public on
  // /contact/; no fabricated NAP.
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "customer support",
    email: "support@ishvaram.com",
    availableLanguage: ["en", "hi"],
  },
  // Real founder + founding year (owner-confirmed 2026-07-23).
  founder: {
    "@type": "Person",
    name: "Digesh Nanda",
  },
  foundingDate: "2025",
  ...(SAME_AS.length > 0 && { sameAs: SAME_AS }),
} as const;

export function organizationSchema() {
  return {
    "@context": "https://schema.org",
    ...ORG,
    description:
      "Vedic astrology tools powered by Swiss Ephemeris — free kundali, rashifal, dosha analysis, muhurat, and AI-guided Jyotish consultations.",
    areaServed: "IN",
    knowsLanguage: ["en", "hi", "sa"],
  };
}

export function mobileApplicationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "MobileApplication",
    "@id": `${SITE_URL}#app`,
    name: SITE_NAME,
    alternateName: "Ishvaram AI Vedic Astrology",
    operatingSystem: "ANDROID, IOS",
    applicationCategory: "LifestyleApplication",
    publisher: { "@id": `${SITE_URL}#org` },
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "INR",
    },
    downloadUrl: [
      "https://play.google.com/store/apps/details?id=com.ishvaram.app",
      "https://apps.apple.com/app/id6743459479",
    ],
  };
}

export function websiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE_URL}#website`,
    url: SITE_URL,
    name: SITE_NAME,
    publisher: { "@id": `${SITE_URL}#org` },
    inLanguage: ["en", "hi"],
  };
}

export interface CollectionPageInput {
  name: string;
  description: string;
  url: string;
  inLanguage?: "en" | "hi";
}

/** CollectionPage schema — for directories/catalogs, never a single-person profile. */
export function collectionPageSchema(input: CollectionPageInput) {
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: input.name,
    description: input.description,
    url: input.url,
    inLanguage: input.inLanguage ?? "en",
    isPartOf: { "@id": `${SITE_URL}#website` },
    publisher: { "@id": `${SITE_URL}#org` },
  };
}

export interface ArticleInput {
  headline: string;
  description: string;
  url?: string;
  datePublished?: string;
  dateModified?: string;
  image?: string;
  inLanguage?: "en" | "hi";
  /**
   * Completed page-level human-review evidence. Never set this from the global
   * reviewer profile alone; the values must match a published ledger entry.
   */
  reviewEvidence?: {
    reviewerSlug: "pandit-kuldeep-shastri";
    reviewedAt: string;
    contentSha256: string;
    sourceCommitSha: string;
  };
}

function boundArticleReview(input: ArticleInput, reviewer: ContentReviewer | null) {
  const evidence = input.reviewEvidence;
  if (!reviewer || !evidence) return null;
  const valid =
    evidence.reviewerSlug === "pandit-kuldeep-shastri" &&
    /^\d{4}-\d{2}-\d{2}/.test(evidence.reviewedAt) &&
    /^[0-9a-f]{64}$/.test(evidence.contentSha256) &&
    /^[0-9a-f]{40,64}$/.test(evidence.sourceCommitSha);
  return valid ? evidence : null;
}

/** Article schema — for explanatory / editorial pages. */
export function articleSchema(input: ArticleInput) {
  const reviewer = activeContentReviewer();
  const boundReview = boundArticleReview(input, reviewer);
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: input.headline,
    description: input.description,
    ...(input.url && { url: input.url, mainEntityOfPage: input.url }),
    ...(input.image && { image: input.image }),
    ...(input.datePublished && { datePublished: input.datePublished }),
    ...(input.dateModified && { dateModified: input.dateModified }),
    inLanguage: input.inLanguage ?? "en",
    author: { "@id": `${SITE_URL}#org` },
    ...(boundReview && {
      reviewedBy: { "@id": CONTENT_REVIEWER_ID },
      dateReviewed: boundReview.reviewedAt.slice(0, 10),
    }),
    publisher: { "@id": `${SITE_URL}#org` },
  };
}

export interface FAQItem {
  question: string;
  answer: string;
}

const SCHEMA_TEXT_ENTITIES: Record<string, string> = {
  amp: "&",
  apos: "'",
  gt: ">",
  hellip: "...",
  laquo: '"',
  ldquo: '"',
  lsaquo: "'",
  lsquo: "'",
  lt: "<",
  mdash: "-",
  ndash: "-",
  nbsp: " ",
  quot: '"',
  raquo: '"',
  rdquo: '"',
  rsaquo: "'",
  rsquo: "'",
};

function decodeSchemaTextEntities(value: string): string {
  return value.replace(/&(#\d+|#x[\da-f]+|[a-z][\da-z]+);/gi, (entity, rawName: string) => {
    const name = rawName.toLowerCase();
    if (name.startsWith("#x")) {
      const codePoint = Number.parseInt(name.slice(2), 16);
      return Number.isNaN(codePoint) || codePoint > 0x10ffff
        ? entity
        : String.fromCodePoint(codePoint);
    }
    if (name.startsWith("#")) {
      const codePoint = Number.parseInt(name.slice(1), 10);
      return Number.isNaN(codePoint) || codePoint > 0x10ffff
        ? entity
        : String.fromCodePoint(codePoint);
    }
    return SCHEMA_TEXT_ENTITIES[name] ?? entity;
  });
}

function plainSchemaText(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const text = decodeSchemaTextEntities(value)
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return text.length > 0 ? text : null;
}

/**
 * FAQPage schema — the single highest-ROI AEO primitive.
 *
 * Rules:
 * - Q and A must be plain text (strip HTML from answer before passing in).
 * - Each answer should be a self-contained, quotable chunk (2–4 sentences).
 * - Every Q&A should contain the page's primary keyword variant so FAQ
 *   additions don't dilute topical focus.
 */
export function faqPageSchema(faqs: FAQItem[]) {
  const seenQuestions = new Set<string>();
  const mainEntity = faqs.flatMap((f) => {
    const question = plainSchemaText(f.question);
    const answer = plainSchemaText(f.answer);
    if (!question || !answer || seenQuestions.has(question)) return [];
    seenQuestions.add(question);
    return [
      {
        "@type": "Question",
        name: question,
        acceptedAnswer: {
          "@type": "Answer",
          text: answer,
        },
      },
    ];
  });

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity,
  };
}

export interface HowToStep {
  name: string;
  text: string;
  url?: string;
}

export interface HowToInput {
  name: string;
  description: string;
  steps: HowToStep[];
  totalTime?: string; // ISO 8601 duration, e.g. "PT2M"
  tool?: string[];
}

/** HowTo schema — for calculator pages ("How to check your Mangal Dosha"). */
export function howToSchema(input: HowToInput) {
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: input.name,
    description: input.description,
    ...(input.totalTime && { totalTime: input.totalTime }),
    ...(input.tool && {
      tool: input.tool.map((t) => ({ "@type": "HowToTool", name: t })),
    }),
    step: input.steps.map((s, i) => ({
      "@type": "HowToStep",
      position: i + 1,
      name: s.name,
      text: s.text,
      ...(s.url && { url: s.url }),
    })),
  };
}

export interface PersonInput {
  name: string;
  jobTitle: string;
  description: string;
  image?: string;
  url?: string;
  sameAs?: string[];
  knowsAbout?: string[];
  credentials?: string[];
  affiliation?: {
    name: string;
    url?: string;
    locality?: string;
    region?: string;
  };
}

/** Build the schema.org affiliation Organization (temple) node, if present. */
function affiliationNode(affiliation: PersonInput["affiliation"]) {
  if (!affiliation) return undefined;
  return {
    "@type": "HinduTemple",
    name: affiliation.name,
    ...(affiliation.url && { url: affiliation.url }),
    ...(affiliation.locality && {
      address: {
        "@type": "PostalAddress",
        addressLocality: affiliation.locality,
        ...(affiliation.region && { addressRegion: affiliation.region }),
        addressCountry: "IN",
      },
    }),
  };
}

/** Person schema — for astrologer profile pages. */
export function personSchema(input: PersonInput) {
  const affiliation = affiliationNode(input.affiliation);
  return {
    "@context": "https://schema.org",
    "@type": "Person",
    name: input.name,
    jobTitle: input.jobTitle,
    description: input.description,
    ...(input.image && { image: input.image }),
    ...(input.url && { url: input.url }),
    ...(input.sameAs && input.sameAs.length > 0 && { sameAs: input.sameAs }),
    ...(input.knowsAbout && { knowsAbout: input.knowsAbout }),
    ...(input.credentials && {
      hasCredential: input.credentials.map((c) => ({
        "@type": "EducationalOccupationalCredential",
        name: c,
      })),
    }),
    ...(affiliation && { affiliation }),
    worksFor: { "@id": `${SITE_URL}#org` },
  };
}

/**
 * Content-reviewer Person node — the named, credentialed human the Article
 * `author`/`reviewedBy` @ids point at (gap: eeat-named-reviewer-schema).
 * Returns `null` unless a REAL reviewer is configured + enabled
 * (content-reviewer.ts), so pages stay Organization-level until the owner fills
 * in a real person. When present, emit it into the page @graph (e.g. via
 * `schemaGraph(...)` / `pageSeoJsonLd`) so the author/reviewedBy reference
 * resolves to a full Person entity with credentials + sameAs.
 */
export function contentReviewerSchema(): Record<string, unknown> | null {
  const reviewer: ContentReviewer | null = activeContentReviewer();
  if (!reviewer) return null;
  return {
    ...personSchema({
      name: reviewer.name,
      jobTitle: reviewer.jobTitle,
      description: reviewer.description,
      image: reviewer.image,
      url: reviewer.url,
      sameAs: reviewer.sameAs,
      knowsAbout: reviewer.knowsAbout,
      credentials: reviewer.credentials,
      affiliation: reviewer.affiliation,
    }),
    "@id": CONTENT_REVIEWER_ID,
  };
}

export interface BreadcrumbEntry {
  name: string;
  url: string;
}

/** BreadcrumbList schema — complements site navigation. */
export function breadcrumbSchema(trail: BreadcrumbEntry[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: trail.map((b, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: b.name,
      item: b.url.startsWith("http") ? b.url : `${SITE_URL}${b.url}`,
    })),
  };
}

/** Collect multiple schemas into a graph — emit as a single JSON-LD block. */
export function schemaGraph(...schemas: Array<Record<string, unknown>>): Record<string, unknown> {
  const graph = schemas.map((s) => {
    const { ["@context"]: _ctx, ...rest } = s;
    return rest;
  });
  return {
    "@context": "https://schema.org",
    "@graph": graph,
  };
}
